/**
 * During the single-user development phase nobody creates or edits vault files
 * by hand: the owner asks a connected AI over MCP and RecallVault shows the
 * result. Where a conventional app would offer a "new note" button, this client
 * hands over a prompt to paste into that AI instead.
 */

export type AskKind = "create" | "revise" | "missing-link" | "search-miss";

export interface AskPrompt {
  readonly title: string;
  readonly explanation: string;
  readonly prompt: string;
}

const VAULT_ROOT = "notes/";

/** Suggests a vault path for a subject the AI has not written yet. */
export function suggestedPath(subject: string, today: string): string {
  const slug = subject
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${VAULT_ROOT}inbox/${today}-${slug || "untitled"}.md`;
}

export function askPrompt(kind: AskKind, subject: string, today: string): AskPrompt {
  const trimmed = subject.trim();
  switch (kind) {
    case "create":
      return {
        title: "Ask your AI to write this note",
        explanation: "RecallVault does not create files. Paste this into the assistant connected to your vault, then press Sync here to read what it wrote.",
        prompt: trimmed
          ? `Using my RecallVault MCP tools, create ${suggestedPath(trimmed, today)} with a note about ${trimmed}. Give it a clear title and link related notes with [[wikilinks]].`
          : `Using my RecallVault MCP tools, list what is already in my vault, then create a new note for what we just discussed. Give it a clear title and link related notes with [[wikilinks]].`,
      };
    case "revise":
      return {
        title: "Ask your AI to change this note",
        explanation: "Edits go through your assistant so every change is one it can explain. Sync afterwards to see the result and its version history here.",
        prompt: `Using my RecallVault MCP tools, read ${trimmed} and revise it. Tell me what you changed and why before you write.`,
      };
    case "missing-link":
      return {
        title: "That note does not exist yet",
        explanation: "A [[link]] points somewhere your vault has nothing. Ask your assistant to write it rather than creating it here.",
        prompt: `Using my RecallVault MCP tools, create the note that [[${trimmed}]] points to. Search the vault first so it fits what is already written.`,
      };
    case "search-miss":
      return {
        title: "Nothing in your vault matches",
        explanation: "Search only reads what is already stored. If this should exist, ask your assistant to write it.",
        prompt: trimmed
          ? `Using my RecallVault MCP tools, search my vault for "${trimmed}". If nothing covers it, create ${suggestedPath(trimmed, today)} about ${trimmed}.`
          : `Using my RecallVault MCP tools, tell me what my vault already covers.`,
      };
  }
}

export interface SearchMiss {
  readonly headline: string;
  readonly detail: string;
}

/**
 * An empty vault has not failed to match anything — it has nothing in it yet.
 * Saying "no match" there reads as a broken search on a first run, so the two
 * cases get two messages.
 */
export function searchMiss(vaultIsEmpty: boolean): SearchMiss {
  return vaultIsEmpty
    ? { headline: "Your vault is empty.", detail: "Nothing has been written here yet. Ask your AI to write the first note, then sync." }
    : { headline: "No match yet.", detail: "Search reads what is already stored. If this should exist, ask your AI to write it." };
}

/** The single sentence the app repeats wherever a create button used to be. */
export const AUTHORSHIP_LINE = "Notes here are written by the AI you connect to your vault. RecallVault is where you read, search, and verify them.";

